/**
 * ABM marketing site — site-wide copy, navigation, footer and asset locations.
 *
 * Copy is ABM's approved content from abmparking.com, reproduced verbatim. It lives here rather
 * than in `messages/en.json` on purpose: `en.json` is sent to the browser for EVERY tenant, and
 * this copy must not ship to (or bloat) any site but ABM's. It is tenant content, like the
 * back-office About Us HTML, and is English-only on the source site.
 */
import { AUTH_ROUTES, MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';
import type { AbmLinkItem } from './abm-content.types';
import { ABM_INDUSTRIES } from './abm-industries.content';
import { ABM_SERVICES } from './abm-services.content';

/** ABM's own CDN — only documents (the ALPR policy PDF) are still linked from here. */
export const ABM_CDN_BASE = 'https://d2syaugtnopsqd.cloudfront.net/wp-content/uploads/sites/3/';

/** Where ABM's images live in this repo (copied from ABM's CDN as WebP). */
export const ABM_IMAGE_BASE = '/tenants/abm/images/';

/** Public URL of an image file under `public/tenants/abm/images/`. */
export function abmImage(file: string): string {
  return `${ABM_IMAGE_BASE}${file}`;
}

/** Public URL of an SVG under `public/tenants/abm/icons/` (copied as-is from abmparking.com's markup). */
export function abmIcon(file: string): string {
  return `/tenants/abm/icons/${file}`;
}

export const ABM_LOGO_SRC = '/tenants/abm/abm-logo-white.svg';
/** Intrinsic size of the logo SVG — abmparking.com draws it at 102x40 (82x32 on phones). */
export const ABM_LOGO_SIZE = { width: 102, height: 40 } as const;

/** The thin white line-art abmparking.com lays over every banner and the booking-steps card. */
export const ABM_HERO_LINES = {
  src: abmIcon('hero-lines.svg'),
  width: 1888,
  height: 1865,
} as const;

export const ABM_EXTERNAL = {
  news: 'https://abmparking.com/news/',
  contact: 'https://abmparking.com/contact/',
  salesInquiries: 'https://abmparking.com/contact/?form=sales-inquiries',
  alprPolicy: `${ABM_CDN_BASE}2025/11/04104618/abm-alpr-policy-2025-11-04.pdf`,
  abm: 'https://abm.com/',
  californiaPrivacy: 'https://www.abm.com/californiaprivacy',
  employeePrivacyEn: 'https://www.abm.com/employee-privacy-notice#english',
  employeePrivacyEs: 'https://www.abm.com/employee-privacy-notice#spanish',
  privacyPolicy: 'https://www.abm.com/privacy-policy/',
  termsOfUse: 'https://www.abm.com/terms-of-use/',
} as const;

export const ABM_CONTACT = {
  phoneLabel: '877-727-5452',
  phoneHref: 'tel:877-727-5452',
  emailLabel: 'contactparking@abm.com',
  emailHref: 'mailto:contactparking@abm.com',
} as const;

/** Header / footer labels and other interface strings. */
export const ABM_COPY = {
  brandName: 'ABM Parking',
  primaryNavAriaLabel: 'Main navigation',
  footerNavAriaLabel: 'Footer navigation',
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
  findParking: 'Find Parking',
  news: 'News',
  industries: 'Industries',
  services: 'Services',
  contact: 'Contact',
  logIn: 'Log In',
  myAccount: 'My Account',
  createAccount: 'Create Account',
  viewAll: 'View all',
  learnMore: 'Learn More',
  opensInNewTab: '(opens in a new tab)',
  alprQuestion: 'Questions about ABM’s use of automated license plate reader technology?',
  alprLink: 'See our policy here.',
} as const;

/** Header "Find Parking" and footer "Find Parking" — the standard reserve screen. */
export const ABM_FIND_PARKING_HREF = MY_ACCOUNT_ROUTES.reserve;
export const ABM_LOGIN_HREF = AUTH_ROUTES.login;
export const ABM_ACCOUNT_HREF = MY_ACCOUNT_ROUTES.overview;

export interface AbmNavGroup {
  label: string;
  href: string;
  items: AbmLinkItem[];
}

export const ABM_NAV_INDUSTRIES: AbmNavGroup = {
  label: ABM_COPY.industries,
  href: '/industries',
  items: ABM_INDUSTRIES.map((page) => ({ label: page.title, href: `/industries/${page.slug}` })),
};

export const ABM_NAV_SERVICES: AbmNavGroup = {
  label: ABM_COPY.services,
  href: '/services',
  items: ABM_SERVICES.map((page) => ({ label: page.title, href: `/services/${page.slug}` })),
};

export const ABM_NAV_NEWS: AbmLinkItem = {
  label: ABM_COPY.news,
  href: ABM_EXTERNAL.news,
  external: true,
};
/** Contact lives on abmparking.com (FAQs + the general / sales forms) and opens in a new tab, like News. */
export const ABM_NAV_CONTACT: AbmLinkItem = {
  label: ABM_COPY.contact,
  href: ABM_EXTERNAL.contact,
  external: true,
};

export const ABM_FOOTER = {
  links: [
    { label: ABM_COPY.findParking, href: ABM_FIND_PARKING_HREF },
    ABM_NAV_NEWS,
    { label: ABM_COPY.logIn, href: ABM_LOGIN_HREF },
    { label: ABM_COPY.createAccount, href: ABM_LOGIN_HREF },
  ] satisfies AbmLinkItem[],
  privacyLinks: [
    { label: 'California Privacy Rights', href: ABM_EXTERNAL.californiaPrivacy, external: true },
    {
      label: 'Employee Privacy Notice (English)',
      href: ABM_EXTERNAL.employeePrivacyEn,
      external: true,
    },
    {
      label: 'Employee Privacy Notice (Spanish)',
      href: ABM_EXTERNAL.employeePrivacyEs,
      external: true,
    },
  ] satisfies AbmLinkItem[],
  /** Bottom row, beside the copyright line. */
  legalLinks: [
    { label: 'Privacy Policy', href: ABM_EXTERNAL.privacyPolicy, external: true },
    { label: 'Terms of Use', href: ABM_EXTERNAL.termsOfUse, external: true },
  ] satisfies AbmLinkItem[],
  /** The about paragraph opens with an "ABM" link and closes with a "www.abm.com" link, as on the source. */
  aboutLead: { label: 'ABM', href: ABM_EXTERNAL.abm, external: true } satisfies AbmLinkItem,
  aboutLink: { label: 'www.abm.com', href: ABM_EXTERNAL.abm, external: true } satisfies AbmLinkItem,
  about:
    ' (NYSE: ABM) is one of the world’s largest providers of facility services and solutions. A driving force for a cleaner, healthier, and more sustainable world, ABM provides essential services and forward-looking performance solutions that improve the spaces and places that matter most. From curbside to rooftop, ABM’s comprehensive services include janitorial, engineering, parking, electrical and lighting, energy and electric vehicle charging infrastructure, HVAC and mechanical, landscape and turf, and mission critical solutions. ABM serves a wide range of industries – from commercial office buildings to universities, airports, hospitals, data centers, manufacturing plants and distribution centers, entertainment venues and more. Founded in 1909, ABM serves over 20,000 clients, with annualized revenue approaching $8 billion and more than 100,000 team members in 350+ offices throughout the United States, United Kingdom, Republic of Ireland, and other international locations. For more information, visit ',
  aboutEnd: '.',
  eoe: 'ABM is an EOE (Minority / Female / Veteran / Disability / Gender Identity / Sexual Orientation) and is committed to working with and providing reasonable accommodation to individuals with disabilities. If you have a disability and need assistance in completing the employment application, please call 888-328-8606. We will provide you with assistance and make a determination on your request for reasonable accommodation on a case-by-case basis.',
} as const;

/** `© ABM Industries Incorporated 2026. All rights reserved.` — the year is the current one, as on the source. */
export function abmCopyright(year: number): string {
  return `© ABM Industries Incorporated ${year}. All rights reserved.`;
}
